export default class ImageResizer {
  constructor(dragCamera, sizeLabel, requestDraw, doChange) {
    this._dragCamera = dragCamera;
    this._sizeLabel = sizeLabel;
    this._requestDraw = requestDraw;
    this._do = doChange;
    this._imageX = 0;  // DragCameraの世界座標における位置
    this._imageY = 0;
    this._imageW = 64;
    this._imageH = 64;
    this._mouseonMarkIndex = -1;  // ドラッグされている印のインデックス値　-1ならドラッグされていない
    this._newRect = null;  // ドラッグで示された新しいキャンバスの左上の座標と幅と高さの4つの値
    this._cursor = '';
    this._sleep = false;
  }

  //--- 定数
  static _DRAWING_SZ = 6;  // 印を描画するサイズ
  static _POINTING_SZ = 24;  // ポインティングに反応するサイズ
  static _DRAWING_OFFSETS = [
    -ImageResizer._DRAWING_SZ, -Math.floor(ImageResizer._DRAWING_SZ / 2), 0
  ];
  static _POINTING_OFFSETS = [
    -ImageResizer._POINTING_SZ, -Math.floor(ImageResizer._POINTING_SZ / 2), 0
  ];
  static RESIZER_CURSOR_RADS = [
    -Math.PI / 4, 0, Math.PI / 4,
    Math.PI / 2, 0, Math.PI / 2,
    Math.PI / 4, 0, -Math.PI / 4,
  ];

  static getCursorCSSValue(rad) {
    return `url('data:image/svg+xml;utf8,<svg width="32" height="32" version="1.1" viewBox="0 0 8.4667 8.4667" xmlns="http://www.w3.org/2000/svg" style="transform-origin:50% 50%; transform: rotate(${rad.toFixed(4)}rad);"><path d="m4.2333 0.13229-2.1167 1.9844h1.4552v4.2333h-1.4552l2.1167 1.9844 2.1167-1.9844h-1.4552v-4.2333h1.4552z" fill="white" stroke="black" stroke-linejoin="bevel" stroke-width=".26458"/></svg>') 16 16,move`;
  }

  setRect(x, y, w, h) {
    this._imageX = x;
    this._imageY = y;
    this._imageW = w;
    this._imageH = h;
  }

  //--- 画像サイズ＝枠線の描画幅を設定しカメラも移動　ChangeTrackerから使用することを想定
  resize(x1, y1, x2, y2, stayPosition = true) {
    this._imageW = x2 - x1 + 1;
    this._imageH = y2 - y1 + 1;
    if(stayPosition) {
      if(x1) this._dragCamera.moveX(-x1);
      if(y1) this._dragCamera.moveY(-y1);
    } else {
      this._imageX = x1;
      this._imageY = y1;
    }
  }

  //--- マウスイベント
  start(cursorX, cursorY) {
    if(!this._sleep && this._mouseonMarkIndex !== -1) {
      this._newRect = [this._imageX, this._imageY, this._imageW, this._imageH];
      this._sizeLabel.update(cursorX, cursorY, this._newRect[2], this._newRect[3]);
      this._sizeLabel.show();
      this._requestDraw();
      return true;
    }
    return false;
  }
  cancel() {
    this._newRect = null;
    this._sizeLabel.hide();
    this._requestDraw();
  }
  drag(cursorX, cursorY) {
    this._newRect = this._calcNewRect(cursorX, cursorY);
    this._sizeLabel.update(cursorX, cursorY, this._newRect[2], this._newRect[3]);
    this._requestDraw();
  }
  nodrag(cursorX, cursorY) {
    if(this._sleep) return;
    let i = this._scanMouseonMarkIndex(cursorX, cursorY);
    this._mouseonMarkIndex = i;
    if(i === -1) {
      this._cursor = '';  // autoではシステムのカーソルが表示され、noneでは全く表示されなかった
    } else {
      this._cursor = ImageResizer.getCursorCSSValue(ImageResizer.RESIZER_CURSOR_RADS[i]);
    }
  }
  finish() {
    const [x1, y1, width, height] = this._newRect;
    if(width !== this._imageW || height !== this._imageH) {
      const x2 = x1 + width - 1;
      const y2 = y1 + height - 1;
      this._do('resize', x1, y1, x2, y2);
    }
    this._newRect = null;
    this._sizeLabel.hide();
    this._requestDraw();
  }

  //---
  getCursor() {
    return this._cursor;
  }
  sleep() {
    this._sleep = true;
    this._cursor = '';
    this._mouseonMarkIndex = -1;
  }
  wake() {
    this._sleep = false;
  }

  //---
  draw(ctx) {
    this._drawGrabbers(ctx);
    this._drawRect(ctx);
  }
  _drawGrabbers(ctx) {
    if(this._sleep) return;
    if(this._newRect !== null) return;
    const style = getComputedStyle(document.documentElement);
    ctx.save();
    ctx.strokeStyle = style.getPropertyValue('--color-frame');
    ctx.lineWidth = 2;
    //ctx.fillStyle = 'white';
    //ctx.globalAlpha = 0.6;
    for(let i = 0; i < 9; ++i) {
      if(i === 4) continue;
      let x = i % 3;
      let y = Math.floor(i / 3);
      let canvasPt = this._dragCamera.worldPtToLocalPtD(this._WIDTH_VARIATION[x], this._HEIGHT_VARIATION[y], false);
      let args = [canvasPt[0] + ImageResizer._DRAWING_OFFSETS[x], canvasPt[1] + ImageResizer._DRAWING_OFFSETS[y], ImageResizer._DRAWING_SZ, ImageResizer._DRAWING_SZ];
      //ctx.fillRect(...args);
      ctx.strokeRect(...args);
    }
    ctx.restore();
  }
  _drawRect(ctx) {
    const style = getComputedStyle(document.documentElement);
    ctx.save();
    ctx.strokeStyle = style.getPropertyValue('--color-frame');
    ctx.lineWidth = 2;
    if(this._sleep || this._newRect === null) {
      ctx.strokeRect(...this._dragCamera.worldPtSzToLocalPtSzD(this._imageX, this._imageY, this._imageW, this._imageH, false));
    } else {
      ctx.setLineDash([8, 4]);
      ctx.strokeRect(...this._dragCamera.worldPtSzToLocalPtSzD(...this._newRect, false));
    }
    ctx.restore();
  }

  //---
  get _WIDTH_VARIATION() {
    return [this._imageX, this._imageX + this._imageW / 2, this._imageX + this._imageW];
  }
  get _HEIGHT_VARIATION() {
    return [this._imageY, this._imageY + this._imageH / 2, this._imageY + this._imageH];
  }
  
  //---
  get _imageX2() {
    return this._imageX + this._imageW - 1; 
  }
  get _imageY2() {
    return this._imageY + this._imageH - 1; 
  }

  //--- マウスカーソルが乗っている印のインデックスを走査　見つからなければ-1
  _scanMouseonMarkIndex(cursorX, cursorY) {
    for(let i = 0; i < 9; ++i) {
      if(i === 4) continue;
      let x = i % 3;
      let y = Math.floor(i / 3);
      let canvasPt = this._dragCamera.worldPtToLocalPtD(this._WIDTH_VARIATION[x], this._HEIGHT_VARIATION[y], true);
      let args = [canvasPt[0] + ImageResizer._POINTING_OFFSETS[x], canvasPt[1] + ImageResizer._POINTING_OFFSETS[y], ImageResizer._POINTING_SZ, ImageResizer._POINTING_SZ];
      if(this._isInside(cursorX, cursorY, ...args)  === true) {
        return i;
      }
    }
    return -1;
  }

  //--- 指定座標が指定範囲内にあるかどうか
  _isInside(x, y, rangeX, rangeY, rangeW, rangeH) {
    return rangeX <= x && x < rangeX + rangeW &&
           rangeY <= y && y < rangeY + rangeH;
  }

  //--- 現在のマウス座標から新しいキャンバスサイズを求める
  _calcNewRect(cursorX, cursorY) {
    const oldX1 = this._imageX;
    const oldY1 = this._imageY;
    const oldX2 = this._imageX2;
    const oldY2 = this._imageY2;
    const underX2 = newX1 => { return newX1 < oldX2 ? newX1 : oldX2; }
    const underY2 = newY1 => { return newY1 < oldY2 ? newY1 : oldY2; }
    const overX1 = newX2 => { return oldX1 < newX2 ? newX2 : oldX1; }
    const overY1 = newY2 => { return oldY1 < newY2 ? newY2 : oldY1; }
    
    let [newX, newY] = this._dragCamera.localPtToWorldPt(cursorX, cursorY).map(Math.floor);
    let rect;
    
    switch(this._mouseonMarkIndex) {
      case 0: rect = [underX2(newX), underY2(newY), oldX2, oldY2]; break;
      case 1: rect = [oldX1, underY2(newY), oldX2, oldY2]; break;
      case 2: rect = [oldX1, underY2(newY), overX1(newX), oldY2]; break;
      case 3: rect = [underX2(newX), oldY1, oldX2, oldY2]; break;
      case 5: rect = [oldX1, oldY1, overX1(newX), oldY2]; break;
      case 6: rect = [underX2(newX), oldY1, oldX2, overY1(newY)]; break;
      case 7: rect = [oldX1, oldY1, oldX2, overY1(newY)]; break;
      case 8: rect = [oldX1, oldY1, overX1(newX), overY1(newY)]; break;
      default: throw 0;
    }
    let [x1, y1, x2, y2] = rect;
    return [x1, y1, x2 - x1 + 1, y2 - y1 + 1];
  }
}
